import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import StatusPill, { STATUS_META } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, PlusCircle, ClipboardText, ChatCircleText } from "@phosphor-icons/react";

export default function AdminRequestDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [statusChange, setStatusChange] = useState("under_review");
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quote, setQuote] = useState({
    interpreted_criteria: "",
    estimated_total_audience: 2500,
    assumptions: "Based on public firmographic filters and standard exclusion of records changed in last 90 days.",
    exclusions: "",
    std_price: 1200,
    ver_price: 2450,
    std_records: 2500,
    ver_records: 2250,
    currency: "USD",
    estimated_delivery_date: "",
    internal_cost: 600,
    internal_margin: 600,
  });
  const sampleRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/requests/${id}`);
    setData(data);
    if (data.request?.interpreted_criteria) setQuote((q) => ({ ...q, interpreted_criteria: data.request.interpreted_criteria }));
    else if (data.request?.plain_english) setQuote((q) => ({ ...q, interpreted_criteria: data.request.plain_english }));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="p-10">Loading…</div>;
  const { request, quotes, messages, internal_notes, sample } = data;

  const changeStatus = async () => {
    await api.post(`/requests/${id}/status`, { status: statusChange });
    toast.success("Status updated"); load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/requests/${id}/notes`, { body: note });
    setNote(""); load();
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    await api.post(`/requests/${id}/messages`, { body: msg });
    setMsg(""); toast.success("Clarification sent"); load();
  };
  const uploadSample = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", "sample"); fd.append("parent_type", "request"); fd.append("parent_id", id);
    await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Sample uploaded"); load();
  };
  const createQuote = async () => {
    const payload = {
      request_id: id,
      interpreted_criteria: quote.interpreted_criteria,
      estimated_total_audience: Number(quote.estimated_total_audience),
      assumptions: quote.assumptions,
      exclusions: quote.exclusions,
      currency: quote.currency,
      estimated_delivery_date: quote.estimated_delivery_date || null,
      internal_cost: Number(quote.internal_cost),
      internal_margin: Number(quote.internal_margin),
      options: [
        { key: "standard", label: "Standard Data", price: Number(quote.std_price), estimated_records: Number(quote.std_records), currency: quote.currency, included_notes: ["Firmographic filter match", "Business email where available"] },
        { key: "verified", label: "Verified Data", price: Number(quote.ver_price), estimated_records: Number(quote.ver_records), currency: quote.currency, included_notes: ["Freshly re-verified emails", "Invalid emails replaced within tolerance"] },
      ],
    };
    await api.post("/quotes", payload);
    toast.success("Quote issued"); setShowQuoteForm(false); load();
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-muted-foreground">{request.reference}</span>
              <StatusPill status={request.status} size="lg" />
            </div>
            <h1 className="font-heading font-black tracking-tighter text-3xl md:text-4xl">{request.title}</h1>
            <p className="text-sm text-muted-foreground font-mono mt-3 whitespace-pre-wrap">{request.plain_english}</p>
          </div>

          {/* Criteria summary */}
          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Interpreted criteria</div>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Countries:</span> {request.criteria.countries.join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Industries:</span> {request.criteria.industries_include.join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Employees:</span> {request.criteria.employee_min || "?"} – {request.criteria.employee_max || "?"}</div>
              <div><span className="text-muted-foreground">Seniorities:</span> {request.criteria.seniorities.join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Titles:</span> {request.criteria.exact_titles.join(", ") || "—"}</div>
              <div><span className="text-muted-foreground">Package:</span> {request.criteria.package_preference}</div>
            </div>
          </div>

          {/* Sample upload */}
          <div className="border border-border bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-semibold flex items-center gap-2"><ClipboardText size={16} weight="duotone" /> Sample file</div>
                <div className="text-xs text-muted-foreground">{sample ? sample.original_filename : "None uploaded yet."}</div>
              </div>
              <input type="file" ref={sampleRef} accept=".csv,.xlsx" className="hidden" onChange={uploadSample} data-testid={TID.admin.uploadSample} />
              <Button size="sm" variant="outline" onClick={() => sampleRef.current?.click()} className="rounded-sm">
                <Upload size={14} className="mr-2" /> Upload sample
              </Button>
            </div>
          </div>

          {/* Existing quotes */}
          {quotes.map((q) => (
            <div key={q.quote_id} className="border border-border bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">Quote v{q.version} — {q.status}</div>
                <StatusPill status={q.status === "approved" ? "awaiting_payment" : "estimate_ready"} />
              </div>
              <div className="text-sm text-muted-foreground mb-3">{q.interpreted_criteria}</div>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {q.options?.map((o) => (
                  <div key={o.key} className="border border-border p-3">
                    <div className="font-semibold">{o.label}</div>
                    <div className="text-lg font-heading font-bold">{q.currency} {o.price?.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{o.estimated_records?.toLocaleString()} records</div>
                  </div>
                ))}
              </div>
              {(q.internal_cost || q.internal_margin) && (
                <div className="mt-3 text-xs font-mono text-muted-foreground">
                  INT. COST: ${q.internal_cost} · MARGIN: ${q.internal_margin}
                </div>
              )}
            </div>
          ))}

          {/* Create quote */}
          {!showQuoteForm && (
            <Button data-testid={TID.admin.createQuote} onClick={() => setShowQuoteForm(true)} className="rounded-sm">
              <PlusCircle size={14} className="mr-2" /> {quotes.length ? "Create revised quote" : "Create estimate & quote"}
            </Button>
          )}
          {showQuoteForm && (
            <div className="border border-border bg-white p-5 space-y-3">
              <div><Label>Interpreted criteria</Label><Textarea data-testid={TID.admin.quoteInterp} rows={3} value={quote.interpreted_criteria} onChange={(e) => setQuote({ ...quote, interpreted_criteria: e.target.value })} className="mt-1" /></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Estimated total audience</Label><Input data-testid={TID.admin.quoteEstVol} type="number" value={quote.estimated_total_audience} onChange={(e) => setQuote({ ...quote, estimated_total_audience: e.target.value })} className="mt-1" /></div>
                <div><Label>Estimated delivery date</Label><Input type="date" value={quote.estimated_delivery_date} onChange={(e) => setQuote({ ...quote, estimated_delivery_date: e.target.value })} className="mt-1" /></div>
                <div><Label>Standard price</Label><Input data-testid={TID.admin.quoteStdPrice} type="number" value={quote.std_price} onChange={(e) => setQuote({ ...quote, std_price: e.target.value })} className="mt-1" /></div>
                <div><Label>Verified price</Label><Input data-testid={TID.admin.quoteVerPrice} type="number" value={quote.ver_price} onChange={(e) => setQuote({ ...quote, ver_price: e.target.value })} className="mt-1" /></div>
                <div><Label>Standard records</Label><Input type="number" value={quote.std_records} onChange={(e) => setQuote({ ...quote, std_records: e.target.value })} className="mt-1" /></div>
                <div><Label>Verified records</Label><Input type="number" value={quote.ver_records} onChange={(e) => setQuote({ ...quote, ver_records: e.target.value })} className="mt-1" /></div>
                <div><Label>Internal cost (staff-only)</Label><Input type="number" value={quote.internal_cost} onChange={(e) => setQuote({ ...quote, internal_cost: e.target.value })} className="mt-1" /></div>
                <div><Label>Internal margin (staff-only)</Label><Input type="number" value={quote.internal_margin} onChange={(e) => setQuote({ ...quote, internal_margin: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Assumptions</Label><Textarea rows={2} value={quote.assumptions} onChange={(e) => setQuote({ ...quote, assumptions: e.target.value })} className="mt-1" /></div>
              <div className="flex gap-2">
                <Button data-testid={TID.admin.quoteSubmit} onClick={createQuote} className="rounded-sm">Issue quote</Button>
                <Button variant="ghost" onClick={() => setShowQuoteForm(false)} className="rounded-sm">Cancel</Button>
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Change status</div>
            <Select value={statusChange} onValueChange={setStatusChange}>
              <SelectTrigger data-testid="admin-status-select" className="mb-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="admin-status-apply" onClick={changeStatus} className="rounded-sm w-full">Apply</Button>
          </div>

          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3 flex items-center gap-2"><ChatCircleText size={14} /> Clarification to customer</div>
            <Textarea data-testid="admin-clarify-input" rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} className="mb-2" placeholder="Ask a question…" />
            <Button data-testid="admin-clarify-send" onClick={sendMsg} className="rounded-sm w-full">Send to customer</Button>
          </div>

          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Internal notes (staff-only)</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mb-2" data-testid="admin-note-input" />
            <Button onClick={addNote} className="rounded-sm w-full" data-testid="admin-note-add">Add note</Button>
            <div className="mt-3 space-y-2">
              {internal_notes.map((n) => (
                <div key={n.note_id} className="text-xs border-l-2 border-primary pl-2 py-1">
                  <div className="text-muted-foreground font-mono">{new Date(n.created_at).toLocaleString()}</div>
                  {n.body}
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border bg-white p-5">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Message history</div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {messages.map((m) => (
                <div key={m.message_id} className="text-xs">
                  <div className="text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground">{m.from_role}</div>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
